import { FocusKeyManager } from '@angular/cdk/a11y';
import { AfterViewInit, Component, EventEmitter, HostListener, Input, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import {
    DEBOUNCE_TIME,
    ResultsPerPage,
    Routes,
    SKU_SEARCH_SUGGESTION_TYPE,
    SkuSearchOptions,
    SuggestionsCount
} from '@emi/shared/constants';
import { SkuSearchService as GetSkus } from '@emi/shared/data-access';
import {
    Attribute, Brand, Category, ISearchSuggestionResponse, Retailer, SearchOption, SearchSuggestionRequest, SubBrand, Suggestion, Supplier
} from '@emi/shared/models';
import {
    DataExtractionState,
    addAttributeSelection, addBrandsSuggestionSelection, addCategoriesSuggestionSelection, addRetailerSelection, addSubBrandsSuggestionSelection, addSuppliersSelection, clearFilters, clearSelections,
    getCountries,
    getExchangeRates
} from '@emi/shared/state/filters';
import { SkuSearchState, SortOptionsByPageState, clearSortOptions, getSkuSearchResults, selectSkuSearchState } from '@emi/shared/state/sku-search';
import { SkuSearchService } from '@emi/sku-search/data-access';
import { Store, select } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { SearchSuggestionComponent } from '../search-suggestion/search-suggestion.component';

@Component({
    selector: 'via-search-bar',
    templateUrl: './search-bar.component.html',
    styleUrls: ['./search-bar.component.scss'],
})
export class SearchBarComponent implements OnInit, AfterViewInit {
    @ViewChild('submitButton')
    public submitButton: HTMLFormElement;

    @ViewChildren(SearchSuggestionComponent)
    public suggestionElements: QueryList<SearchSuggestionComponent>;

    @Input()
    public matSelectPanelClass = '';

    @Input()
    public placeholderText = 'Type to start searching...';

    private searchSubject$ = new Subject<string>();
    private componentClicked = false;
    private searchRequest: SearchSuggestionRequest = new SearchSuggestionRequest();
    private skuSearchSubscription: Subscription;
    public selectedSuggestion: Suggestion;
    public MINIMUM_SEARCH_TERM_LENGTH = 3;
    public options: Array<SearchOption> = SkuSearchOptions;
    public searchTerm = '';
    public keyManager: FocusKeyManager<SearchSuggestionComponent>;
    public suggestions: Array<Suggestion>;
    public loading = false;
    public showingSuggestionsContainer = false;
    public showingSuggestions = false;
    public suggestionsCount = 0;
    public selectedOption!: SearchOption;
    public selectedCurrency: string;

    constructor(
        private router: Router,
        private store: Store<SkuSearchState>,
        private getSkusService: GetSkus,
        private skuSearchService: SkuSearchService,
        private extractionStore: Store<DataExtractionState>,
        private sortOptionsByPageStore: Store<SortOptionsByPageState>
    ) { }

    // Click event outside the component.
    @HostListener('document:click')
    public onDocumentClick(): void {
        if (!this.componentClicked) {
            this.hideSuggestionsContainer();
        }
        this.componentClicked = false;
    }

    @HostListener('click')
    public onComponentClick(): void {
        this.componentClicked = true;
    }

    public ngOnInit(): void {
        this.selectedOption = this.options[0];
        this.searchRequest.resultsPerPage = ResultsPerPage;
        this.getSuggestionList();
    }

    public ngAfterViewInit(): void {
        this.keyManager = new FocusKeyManager(this.suggestionElements).withWrap();
    }

    public resetSearch(): void {
        this.searchTerm = '';
        this.selectedSuggestion = {} as Suggestion;
        this.showingSuggestionsContainer = false;
        this.showingSuggestions = false;
    }

    public showSuggestionsContainer(): void {
        this.showingSuggestionsContainer = true;
    }

    public hideSuggestionsContainer(): void {
        if (this.showingSuggestionsContainer) {
            this.showingSuggestionsContainer = false;
        }
    }
    public getSearchSuggestions(): void {
        if (!this.showingSuggestionsContainer) {
            this.showSuggestionsContainer();
        }
        if (this.transformSearchTerm().length >= this.MINIMUM_SEARCH_TERM_LENGTH) {
            this.searchSubject$.next(this.transformSearchTerm());
            this.showingSuggestions = false;
            this.loading = true;
        } else {
            this.showingSuggestions = false;
        }
    }

    public suggestionSearch(suggestion: Suggestion) {
        this.selectedSuggestion = suggestion;
        this.searchTerm = suggestion.text;

        this.skuSearchSubscription = this.store.pipe(select(selectSkuSearchState)).subscribe((state) => {
            this.selectedCurrency = state.currency;
        });
        this.extractionStore.dispatch(getExchangeRates({ currency: this.selectedCurrency }));

        this.submitSuggestionSearch();
    }

    public isSubmitButtonDisabled(): boolean {
        return this.searchTerm.length < this.MINIMUM_SEARCH_TERM_LENGTH;
    }

    // Key events for the search input field.
    public inputKeyEvents(event: KeyboardEvent): void {
        if (event.key === 'Enter' && this.searchTerm.length >= this.MINIMUM_SEARCH_TERM_LENGTH) {
            this.submitSearch();
        } else if (event.key === 'Escape' || this.searchTerm.length === 0) {
            this.hideSuggestionsContainer();
        }
        // Focus on the first suggestion.
        else if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.keyManager.setFirstItemActive();
        }
    }

    // Key events for the search suggestions list.
    public suggestionsKeyEvents(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.hideSuggestionsContainer();
        } else if (event.key === 'Tab') {
            event.preventDefault();
            this.hideSuggestionsContainer();
            this.submitButton.focus();
        }
        // Cycle up or down through the list of suggestions.
        else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            this.keyManager.onKeydown(event);
        }
    }

    public submitSearch() {
        this.sortOptionsByPageStore.dispatch(clearSortOptions());
        this.store.dispatch(clearSelections());
        this.extractionStore.dispatch(clearFilters());
        this.getSkusService.updateSearchFilterText(true);
        this.store.dispatch(
            getSkuSearchResults({
                searchTerm: this.searchTerm,
                searchOption: {
                    label: this.selectedOption.label,
                    value: this.selectedOption.value,
                    searchDimension: this.selectedOption.label,
                },
                includeSearchPhrase: true,
            })
        );
        this.store.dispatch(getCountries());
        this.skuSearchSubscription = this.store.pipe(select(selectSkuSearchState)).subscribe((state) => {
            this.selectedCurrency = state.currency;
        });
        this.extractionStore.dispatch(getExchangeRates({ currency: this.selectedCurrency }));

        this.hideSuggestionsContainer();
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            this.router.navigate([Routes.SKU_SEARCH_RESULTS]);
        });
    }

    public submitSuggestionSearch() {
         this.sortOptionsByPageStore.dispatch(clearSortOptions());
        const isSku = this.isSkuSuggestionType(this.selectedSuggestion.type);
        this.store.dispatch(clearSelections());
        this.extractionStore.dispatch(clearFilters());
        this.addSearchFilter();
        this.getSkusService.updateSearchFilterText(true);
        this.store.dispatch(
            getSkuSearchResults({
                searchTerm: this.selectedSuggestion.text,
                searchOption: {
                    label: this.selectedOption.label,
                    value: this.selectedOption.value,
                    searchDimension: this.selectedSuggestion.type,
                },
                includeSearchPhrase: isSku,
            })
        );
        this.store.dispatch(getCountries());
        this.hideSuggestionsContainer();
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            this.router.navigate([Routes.SKU_SEARCH_RESULTS]);
        });
    }

    private isSkuSuggestionType(suggestionType: string): boolean {
        if (suggestionType == SKU_SEARCH_SUGGESTION_TYPE.skus || suggestionType == '') {
            return true;
        }
        return false;
    }

    private getSuggestionList(): void {
        this.searchSubject$
            .pipe(
                debounceTime(DEBOUNCE_TIME),
                switchMap((searchWord: string) => {
                    this.searchRequest.searchType = this.selectedOption.value;
                    this.searchRequest.searchPhrase = searchWord;
                    return this.skuSearchService.getSearchSuggestions(this.searchRequest);
                })
            )
            .subscribe((suggestions) => {
                this.showSuggestionsContainer();
                this.mapDataToSuggestionList(suggestions);
                this.loading = false;
                this.showingSuggestions = true;
                this.suggestionsCount = this.suggestions?.length;
            });
    }

    private mapDataToSuggestionList(suggestions: ISearchSuggestionResponse): void {
        switch (this.searchRequest.searchType) {
            case 1:
                this.mapAllSkuSearchOptions(suggestions);
                break;
            case 2:
                this.suggestions = this.mapSkuSearchInfo(suggestions, 'categories');
                break;
            case 3:
                this.suggestions = this.mapSkuSearchInfo(suggestions, 'suppliers');
                break;
            case 4:
                this.suggestions = this.mapSkuSearchInfo(suggestions, 'brands');
                break;
            case 5:
                this.suggestions = this.mapSkuSearchInfo(suggestions, 'skus');
                break;
            case 6:
                this.suggestions = this.mapSkuSearchInfo(suggestions, 'attributes');
                break;
            case 7:
                this.suggestions = this.mapSkuSearchInfo(suggestions, 'retailers');
                break;
            default:
                this.mapAllSkuSearchOptions(suggestions);
                break;
        }
    }

    private mapAllSkuSearchOptions(suggestions: ISearchSuggestionResponse): void {
        const suggestionArray: Array<Suggestion> = [];
        for (const index in suggestions) {
            switch (index) {
                case 'categories':
                    suggestionArray.push(...this.mapSkuSearchInfo(suggestions, index));
                    break;
                case 'suppliers':
                    suggestionArray.push(...this.mapSkuSearchInfo(suggestions, index));
                    break;
                case 'brands':
                    suggestionArray.push(...this.mapSkuSearchInfo(suggestions, index));
                    break;
                case 'retailers':
                    suggestionArray.push(...this.mapSkuSearchInfo(suggestions, index));
                    break;
                case 'attributes':
                    suggestionArray.push(...this.mapSkuSearchInfo(suggestions, index));
                    break;
                case 'skus':
                    suggestions.skus.forEach((item, index) => {
                        if (item.name) {
                            suggestionArray.push({
                                id: item.id,
                                text: `${item.name}`,
                                textWithHighlight: `${item.name}`,
                                type: index === 0 ? SKU_SEARCH_SUGGESTION_TYPE['skus'] : '',
                            });
                        }
                    });
                    break;
            }
        }

        this.suggestions = suggestionArray;
    }

    private transformSearchTerm(): string {
        return this.searchTerm.trim();
    }

    private mapSkuSearchInfo(suggestions: any, index: string): Array<Suggestion> {
        const suggestionArray: Array<Suggestion> = [];
        if (this.searchRequest.searchType === 1) {
            if (suggestions[index][0]?.name.length > 0) {
                suggestionArray.push({
                    id: suggestions[index][0]?.id,
                    text: suggestions[index][0]?.name,
                    textWithHighlight: suggestions[index][0]?.name,
                    type: SKU_SEARCH_SUGGESTION_TYPE[index],
                    isSubType: suggestions[index][0]?.isSubType,
                });
            }
        } else {
            suggestions[index]?.slice(0, SuggestionsCount).forEach((item: any, subIndex: number) => {
                suggestionArray.push({
                    id: item?.id,
                    text: item?.name,
                    textWithHighlight: item?.name,
                    type: SKU_SEARCH_SUGGESTION_TYPE[index],
                    isSubType: suggestions[index][subIndex]?.isSubType,
                });
            });
        }
        return suggestionArray;
    }

    private addSearchFilter(): void {
        const type = this.selectedSuggestion?.type;
        switch (type) {
            case SKU_SEARCH_SUGGESTION_TYPE['brands']:
                this.addBrandFilter();
                break;
            case SKU_SEARCH_SUGGESTION_TYPE['suppliers']:
                this.addSupplierFilter();
                break;
            case SKU_SEARCH_SUGGESTION_TYPE['retailers']:
                this.addRetailerFilter();
                break;
            case SKU_SEARCH_SUGGESTION_TYPE['attributes']:
                this.addAttributesFilter();
                break;
            case SKU_SEARCH_SUGGESTION_TYPE['categories']:
                this.addCategoryFilter();
                break;
            case SKU_SEARCH_SUGGESTION_TYPE['skus']:
                break;
            default:
                return;
        }
    }

    private addBrandFilter(): void {
        if (this.selectedSuggestion?.isSubType) {
            const subBrands: Array<SubBrand> = [new SubBrand(this.selectedSuggestion.id, this.selectedSuggestion.text)];
            this.extractionStore.dispatch(addSubBrandsSuggestionSelection({ subBrands }));
        } else {
            const brands: Array<Brand> = [new Brand(this.selectedSuggestion.id, this.selectedSuggestion.text, undefined, true)];
            this.extractionStore.dispatch(addBrandsSuggestionSelection({ brands }));
        }
    }

    private addSupplierFilter(): void {
        const suppliers: Array<Supplier> = [new Supplier(this.selectedSuggestion.id, this.selectedSuggestion.text)];
        this.extractionStore.dispatch(addSuppliersSelection({ suppliers }));
    }

    private addRetailerFilter(): void {
        const retailers: Array<Retailer> = [new Retailer(this.selectedSuggestion.id, this.selectedSuggestion.text)];
        this.extractionStore.dispatch(addRetailerSelection({ retailers }));
    }

    private addAttributesFilter(): void {
        const attributes: Array<Attribute> = [new Attribute(this.selectedSuggestion.id, this.selectedSuggestion.text)];
        this.extractionStore.dispatch(addAttributeSelection({ attributes }));
    }

    private addCategoryFilter(): void {
        const categories: Array<Category> = [new Category(this.selectedSuggestion.id, this.selectedSuggestion.text)];
        this.extractionStore.dispatch(addCategoriesSuggestionSelection({ categories }));
    }
}
